// ClientPortal.jsx - The luxe client-facing portal
// URL: /me/{token}
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "./supabase";
import { fmtAppointmentDate } from "./dateUtils";

const C = {
  bg: "#fafaf7", cream: "#f8f5f0", surface: "#ffffff", border: "#e8e4df",
  borderGold: "rgba(180,140,60,0.3)", goldBg: "rgba(180,140,60,0.06)",
  gold: "#b48c3c", goldD: "#8a6f2e", goldL: "#d4b572",
  green: "#2e8b57", greenL: "rgba(46,139,87,0.10)",
  red: "#c0392b", redL: "rgba(192,57,43,0.10)",
  blue: "#2563eb", blueL: "rgba(37,99,235,0.08)",
  purple: "#7c3aed", purpleL: "rgba(124,58,237,0.08)",
  text: "#1a1a2e", muted: "#6b7280", dim: "#9ca3af", light: "#f5f1ec",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("home");

  useEffect(() => {
    if (!token) { setError("Invalid link"); setLoading(false); return; }
    loadData();
  }, [token]);

  useEffect(() => {
    if (!document.getElementById("portal-fonts")) {
      const link = document.createElement("link");
      link.id = "portal-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Cormorant+Garamond:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const loadData = async () => {
    try {
      const { data: result, error } = await supabase.functions.invoke("portal-api", { body: { action: "validate", token } });
      if (error || result?.error) throw new Error(result?.error || error.message);
      setData(result);
    } catch (e) { setError(e.message || "Failed to load"); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div style={{minHeight:"100vh",background:C.cream,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F}}>
      <div style={{textAlign:"center"}}>
        <div style={{width:40,height:40,border:"3px solid "+C.gold,borderTopColor:"transparent",borderRadius:"50%",margin:"0 auto 16px",animation:"spin 1s linear infinite"}}/>
        <div style={{fontSize:13,color:C.muted}}>Loading your account...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{minHeight:"100vh",background:C.cream,display:"flex",alignItems:"center",justifyContent:"center",padding:20,fontFamily:F}}>
      <div style={{maxWidth:400,textAlign:"center",background:"#fff",padding:40,borderRadius:16,border:"1px solid "+C.border}}>
        <div style={{fontSize:48,marginBottom:16}}>🔒</div>
        <div style={{fontSize:22,fontWeight:700,color:C.text,fontFamily:S,marginBottom:8}}>Access Issue</div>
        <div style={{fontSize:13,color:C.muted,marginBottom:20}}>{error === "Token expired" ? "This link has expired. Please request a new link." : error === "Invalid token" ? "This link is invalid." : error}</div>
        <a href="https://www.elyonhq.com/book" style={{display:"inline-block",padding:"12px 24px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",borderRadius:50,textDecoration:"none",fontSize:13,fontWeight:700}}>Book an Appointment →</a>
      </div>
    </div>
  );

  const { client, appointments, payments, packages, memberships, signed_consents, pending_consents, spa } = data;
  const firstName = client.first_name || "there";
  const today = new Date(new Date().toISOString().split("T")[0]);
  const upcomingAppts = appointments.filter(a => new Date(a.date) >= today && a.status !== "cancelled" && a.status !== "no_show");
  const pastAppts = appointments.filter(a => new Date(a.date) < today || a.status === "cancelled");
  const totalSpent = payments.filter(p => p.status === "succeeded").reduce((s, p) => s + (+p.amount || 0), 0);
  const activePackages = packages.filter(p => (p.sessions_remaining || 0) > 0);
  const activeMemberships = memberships.filter(m => m.status === "active");

  return (
    <div style={{minHeight:"100vh",background:C.cream,fontFamily:F,paddingBottom:30}}>
      {/* Hero */}
      <div style={{background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",padding:"40px 24px 80px",textAlign:"center",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,background:"radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)"}}/>
        <div style={{position:"relative",maxWidth:600,margin:"0 auto"}}>
          <div style={{fontSize:11,letterSpacing:"0.2em",opacity:0.85,marginBottom:6,fontWeight:600}}>{(spa.name || "NOVA MED SPA").toUpperCase()}</div>
          <div style={{fontSize:32,fontFamily:S,fontWeight:600,marginBottom:4}}>Hello, {firstName} ✨</div>
          <div style={{fontSize:13,opacity:0.9}}>Welcome to your private account</div>
        </div>
      </div>

      {/* Pending consents alert */}
      {pending_consents.length > 0 && (
        <div style={{maxWidth:760,margin:"-50px auto 12px",padding:"0 16px"}}>
          <div onClick={() => setTab("consents")} style={{background:"linear-gradient(135deg, rgba(220,38,38,0.12), rgba(220,38,38,0.04))",border:"2px solid "+C.red,borderRadius:12,padding:"14px 18px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.red,marginBottom:3}}>📋 {pending_consents.length} consent form{pending_consents.length > 1 ? "s" : ""} need your signature</div>
              <div style={{fontSize:11,color:C.muted}}>Required before your next visit</div>
            </div>
            <span style={{fontSize:13,fontWeight:700,color:C.red}}>Sign Now →</span>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{maxWidth:760,margin: pending_consents.length > 0 ? "0 auto 30px" : "-50px auto 30px",padding:"0 16px"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))",gap:10}}>
          <StatCard icon="📅" label="Upcoming" value={upcomingAppts.length} color={C.gold}/>
          <StatCard icon="✅" label="Visits" value={pastAppts.filter(a => a.status === "completed" || a.status === "confirmed").length} color={C.green}/>
          <StatCard icon="📦" label="Packages" value={activePackages.length} color={C.purple}/>
          <StatCard icon="📋" label="Forms" value={signed_consents.length} color={C.blue}/>
        </div>
      </div>

      {/* Tabs */}
      <div style={{maxWidth:760,margin:"0 auto",padding:"0 16px"}}>
        <div style={{display:"flex",gap:4,background:"#fff",padding:5,borderRadius:12,border:"1px solid "+C.border,marginBottom:18,overflow:"auto"}}>
          {[
            ["home", "🏠 Home"],
            ["appointments", "📅 Visits"],
            ["packages", "📦 Packages"],
            ["consents", `📋 Forms${pending_consents.length > 0 ? " (" + pending_consents.length + ")" : ""}`],
            ["payments", "💳 Payments"],
            ["profile", "👤 Profile"],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex:1,padding:"10px 8px",
              background: tab === id ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : "transparent",
              color: tab === id ? "#fff" : C.muted,
              border:"none",borderRadius:8,fontSize:11,fontWeight: tab === id ? 700 : 600,
              cursor:"pointer",fontFamily:F,whiteSpace:"nowrap",transition:"all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {tab === "home" && <HomeTab client={client} upcomingAppts={upcomingAppts} packages={activePackages} memberships={activeMemberships} totalSpent={totalSpent} setTab={setTab}/>}
        {tab === "appointments" && <AppointmentsTab appointments={appointments} token={token} onReload={loadData}/>}
        {tab === "packages" && <PackagesTab packages={packages} memberships={memberships}/>}
        {tab === "consents" && <ConsentsTab token={token} signedConsents={signed_consents} pendingConsents={pending_consents} clientName={`${client.first_name || ""} ${client.last_name || ""}`.trim()} onSigned={loadData}/>}
        {tab === "payments" && <PaymentsTab payments={payments} totalSpent={totalSpent}/>}
        {tab === "profile" && <ProfileTab client={client} token={token} onUpdate={loadData}/>}
      </div>

      <div style={{marginTop:50,padding:"24px 16px",textAlign:"center",color:C.muted,fontSize:11,borderTop:"1px solid "+C.border}}>
        <div style={{marginBottom:8,fontFamily:S,fontSize:16,color:C.text}}>{spa.name}</div>
        {spa.phone && <div style={{marginBottom:4}}>📞 {spa.phone}</div>}
        {spa.address && <div style={{fontSize:10,opacity:0.7}}>{spa.address}</div>}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color }) {
  return (
    <div style={{background:"#fff",borderRadius:14,padding:"16px 14px",border:"1px solid "+C.border,borderTop:"3px solid "+color,boxShadow:"0 4px 12px rgba(0,0,0,0.04)",textAlign:"center"}}>
      <div style={{fontSize:22,marginBottom:4}}>{icon}</div>
      <div style={{fontSize:26,fontWeight:800,color:C.text,fontFamily:S,lineHeight:1}}>{value}</div>
      <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginTop:4}}>{label}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function HomeTab({ client, upcomingAppts, packages, memberships, totalSpent, setTab }) {
  const nextAppt = upcomingAppts[0];
  return (
    <div>
      {nextAppt && (
        <div style={{background:"#fff",borderRadius:14,padding:"20px",border:"1px solid "+C.border,marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em"}}>Next Appointment</div>
            <span style={{fontSize:10,padding:"3px 10px",background:C.greenL,color:C.green,borderRadius:50,fontWeight:700}}>UPCOMING</span>
          </div>
          <div style={{fontSize:22,fontWeight:700,color:C.text,fontFamily:S,marginBottom:6}}>{nextAppt.treat || "Visit"}</div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>📅 Date</div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{fmtAppointmentDate(nextAppt.date,{weekday:"long",month:"long",day:"numeric"})}</div></div>
            <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>⏰ Time</div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{nextAppt.time || ""}</div></div>
            {nextAppt.dur && <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>⏱ Duration</div><div style={{fontSize:13,fontWeight:600,color:C.text}}>{nextAppt.dur} min</div></div>}
          </div>
        </div>
      )}

      {!nextAppt && (
        <div style={{background:"linear-gradient(135deg,"+C.goldBg+", rgba(255,255,255,0.5))",borderRadius:14,padding:"30px 20px",border:"1px solid "+C.borderGold,marginBottom:14,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>✨</div>
          <div style={{fontSize:18,fontWeight:600,color:C.text,fontFamily:S,marginBottom:6}}>No upcoming appointments</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:16}}>Time to book your next glow up?</div>
          <a href="https://www.elyonhq.com/book" style={{display:"inline-block",padding:"12px 28px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",textDecoration:"none",borderRadius:50,fontSize:13,fontWeight:700,letterSpacing:"0.05em"}}>Book Now →</a>
        </div>
      )}

      {packages.length > 0 && (
        <div style={{background:"#fff",borderRadius:14,padding:"18px 20px",border:"1px solid "+C.border,marginBottom:14}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em"}}>📦 Active Packages</div>
            <button onClick={() => setTab("packages")} style={{background:"none",border:"none",color:C.gold,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F}}>View All →</button>
          </div>
          {packages.slice(0, 2).map(p => (
            <div key={p.id} style={{padding:"12px 14px",background:C.goldBg,borderRadius:9,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{p.packages?.name || "Package"}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:2}}>{p.sessions_used || 0} used</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:22,fontWeight:800,color:C.gold,fontFamily:S,lineHeight:1}}>{p.sessions_remaining || 0}</div>
                <div style={{fontSize:9,color:C.muted}}>LEFT</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {memberships.length > 0 && (
        <div style={{background:"linear-gradient(135deg, "+C.purpleL+", #fff)",borderRadius:14,padding:"18px 20px",border:"1px solid "+C.purple+"30",marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:C.purple,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>💎 Membership</div>
          {memberships.map(m => (
            <div key={m.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontSize:16,fontWeight:700,color:C.text,fontFamily:S}}>{m.membership_plans?.name || "Member"}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:2}}>${m.membership_plans?.monthly_price || 0}/mo · Active</div>
              </div>
              <span style={{fontSize:24}}>✨</span>
            </div>
          ))}
        </div>
      )}

      {totalSpent > 0 && (
        <div style={{background:"#fff",borderRadius:14,padding:"16px 20px",border:"1px solid "+C.border,textAlign:"center"}}>
          <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>You're a Valued Client</div>
          <div style={{fontSize:13,color:C.text,marginBottom:6}}>Member since {new Date(client.created_at).toLocaleDateString("en-US",{month:"long",year:"numeric"})}</div>
          <div style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>Thank you for being part of our family ✨</div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
function AppointmentsTab({ appointments, token, onReload }) {
  const [filter, setFilter] = useState("upcoming");
  const [actionAppt, setActionAppt] = useState(null);
  const [actionType, setActionType] = useState(null); // 'reschedule' | 'cancel' | null
  const today = new Date(new Date().toISOString().split("T")[0]);
  const upcoming = appointments.filter(a => new Date(a.date) >= today && a.status !== "cancelled");
  const past = appointments.filter(a => new Date(a.date) < today || a.status === "cancelled" || a.status === "completed");
  const list = filter === "upcoming" ? upcoming : past;

  const confirmAppt = async (a) => {
    try {
      const { data: result } = await supabase.functions.invoke("portal-api", {
        body: { action: "confirm_appointment", token, appointment_id: a.id }
      });
      const r = typeof result === "string" ? JSON.parse(result) : result;
      if (r.ok) {
        alert("✓ Confirmed!");
        if (onReload) onReload();
      } else {
        alert("Failed: " + (r.error || "unknown"));
      }
    } catch (e) {
      alert("Failed: " + e.message);
    }
  };

  const cancelAppt = async (a) => {
    // 24-hour cancellation policy — anything closer requires a phone call
    // so the front desk can try to back-fill the slot. The portal blocks the
    // self-serve cancel and directs the client to the office number.
    const apptDateTime = new Date(`${a.date}T${a.time || "00:00"}`);
    const hoursUntil = (apptDateTime.getTime() - Date.now()) / (1000 * 60 * 60);
    if (hoursUntil < 24) {
      alert(
        "We can't cancel this online — your appointment is less than 24 hours away.\n\n" +
        "Please call us at 516-543-3940 so we can update the schedule and try to fill the slot. 💛"
      );
      return;
    }

    if (!window.confirm(`Cancel your ${a.treat || "appointment"} on ${fmtAppointmentDate(a.date)}?`)) return;
    try {
      const { data: result } = await supabase.functions.invoke("portal-api", {
        body: { action: "cancel_appointment", token, appointment_id: a.id }
      });
      const r = typeof result === "string" ? JSON.parse(result) : result;
      if (r.ok) {
        alert("Cancelled. You'll get a confirmation by SMS and email.");
        if (onReload) onReload();
      } else {
        alert("Failed: " + (r.error || "unknown"));
      }
    } catch (e) {
      alert("Failed: " + e.message);
    }
  };

  return (
    <div>
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        <button onClick={() => setFilter("upcoming")} style={tabBtn(filter === "upcoming")}>Upcoming ({upcoming.length})</button>
        <button onClick={() => setFilter("past")} style={tabBtn(filter === "past")}>Past ({past.length})</button>
      </div>

      {list.length === 0 ? (
        <div style={{padding:40,textAlign:"center",background:"#fff",borderRadius:14,border:"1px dashed "+C.border}}>
          <div style={{fontSize:36,marginBottom:8}}>📅</div>
          <div style={{fontSize:13,color:C.muted}}>No {filter} appointments</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {list.map(a => {
            const isUpcoming = filter === "upcoming" && a.status !== "cancelled" && a.status !== "completed";
            const confirmed = a.customer_confirmed;
            return (
              <div key={a.id} style={{background:"#fff",borderRadius:12,padding:"14px 16px",border:"1px solid "+(confirmed?"#22c55e":C.border),borderLeft:"3px solid "+(a.status === "completed" ? C.green : a.status === "cancelled" ? C.red : confirmed?"#22c55e":C.gold)}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      {a.treat || "Visit"}
                      {confirmed && <span style={{fontSize:9,padding:"2px 8px",background:"#e6f9ec",color:"#15803d",borderRadius:50,fontWeight:700,border:"1px solid #22c55e"}}>✓ CONFIRMED</span>}
                    </div>
                    <div style={{fontSize:11,color:C.muted}}>{fmtAppointmentDate(a.date,{weekday:"short",month:"short",day:"numeric",year:"numeric"})}{a.time && <> · {a.time}</>}{a.dur && <> · {a.dur} min</>}</div>
                  </div>
                  <span style={{fontSize:9,padding:"2px 8px",background: a.status === "completed" ? C.greenL : a.status === "cancelled" ? C.redL : C.goldBg, color: a.status === "completed" ? C.green : a.status === "cancelled" ? C.red : C.gold,borderRadius:50,fontWeight:700,textTransform:"uppercase"}}>{a.status || "scheduled"}</span>
                </div>
                {a.price && +a.price > 0 && <div style={{fontSize:12,color:C.gold,fontWeight:700,marginTop:6}}>${(+a.price).toFixed(2)} {a.paid && <span style={{fontSize:10,color:C.green,marginLeft:6}}>✓ Paid</span>}</div>}
                {/* ★ DO NOT render a.notes here — appointment notes are an
                    internal staff field (medical history flags, "be careful
                    with this client", "prefers cash", etc.). Owner explicitly
                    asked never to surface them in the client portal. */}

                {/* Action buttons for upcoming */}
                {isUpcoming && (
                  <div style={{display:"flex",gap:6,marginTop:12,flexWrap:"wrap"}}>
                    {!confirmed && (
                      <button onClick={()=>confirmAppt(a)} style={{flex:"1 0 100px",padding:"8px 12px",background:"#22c55e",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>✓ I'll be there</button>
                    )}
                    <button onClick={()=>{setActionAppt(a);setActionType("reschedule");}} style={{flex:"1 0 100px",padding:"8px 12px",background:"#fff",color:C.text,border:"1px solid "+C.border,borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>🔄 Reschedule</button>
                    <button onClick={()=>cancelAppt(a)} style={{flex:"1 0 100px",padding:"8px 12px",background:"#fff",color:"#dc2626",border:"1px solid #fca5a5",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer"}}>✕ Cancel</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{marginTop:18,textAlign:"center"}}>
        <a href="https://www.elyonhq.com/book" style={{display:"inline-block",padding:"12px 28px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",textDecoration:"none",borderRadius:50,fontSize:13,fontWeight:700}}>+ Book New Appointment</a>
      </div>

      {/* Reschedule Modal */}
      {actionAppt && actionType === "reschedule" && (
        <RescheduleModal
          appt={actionAppt} token={token}
          onClose={()=>{setActionAppt(null);setActionType(null);}}
          onDone={()=>{setActionAppt(null);setActionType(null);if(onReload)onReload();}}
        />
      )}
    </div>
  );
}

function RescheduleModal({ appt, token, onClose, onDone }) {
  const [newDate, setNewDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadSlots = async (date) => {
    setLoading(true);
    setSlots([]);
    try {
      const { data: result } = await supabase.functions.invoke("portal-api", {
        body: { action: "get_available_slots", token, date }
      });
      const r = typeof result === "string" ? JSON.parse(result) : result;
      if (r.ok) setSlots(r.slots || []);
      else setError(r.error || "Failed to load slots");
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const submit = async () => {
    if (!newDate || !selectedTime) { alert("Pick a date and time"); return; }
    setLoading(true);
    try {
      const { data: result } = await supabase.functions.invoke("portal-api", {
        body: { action: "reschedule_appointment", token, appointment_id: appt.id, new_date: newDate, new_time: selectedTime }
      });
      const r = typeof result === "string" ? JSON.parse(result) : result;
      if (r.ok) {
        alert("✓ Rescheduled! You'll get a confirmation by SMS and email.");
        if (onDone) onDone();
      } else {
        alert("Failed: " + (r.error || "unknown"));
      }
    } catch (e) {
      alert("Failed: " + e.message);
    }
    setLoading(false);
  };

  // Get tomorrow's date for min
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const maxDate = new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10);

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:16}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:14,padding:24,maxWidth:420,width:"100%",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{fontSize:18,fontWeight:700,marginBottom:6}}>Reschedule {appt.treat}</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:18}}>Currently on {fmtAppointmentDate(appt.date,{weekday:"long",month:"long",day:"numeric"})} at {appt.time}</div>

        <label style={{fontSize:11,color:C.muted,fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase"}}>New Date</label>
        <input type="date" min={tomorrow} max={maxDate} value={newDate} onChange={e=>{setNewDate(e.target.value); if(e.target.value) loadSlots(e.target.value);}} style={{width:"100%",padding:"10px 12px",fontSize:14,border:"1px solid "+C.border,borderRadius:8,marginTop:6,marginBottom:16}}/>

        {newDate && (
          <>
            <label style={{fontSize:11,color:C.muted,fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase"}}>Available Times</label>
            {loading && <div style={{fontSize:12,color:C.muted,padding:"10px 0"}}>Loading available slots...</div>}
            {!loading && slots.length === 0 && <div style={{fontSize:12,color:C.red,padding:"10px 0"}}>No slots available on this date. Pick another.</div>}
            {!loading && slots.length > 0 && (
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(80px, 1fr))",gap:6,marginTop:6,marginBottom:16}}>
                {slots.map(t => {
                  const [h, m] = t.split(":").map(Number);
                  const ampm = h >= 12 ? "PM" : "AM";
                  const hh = h % 12 || 12;
                  const label = `${hh}:${String(m).padStart(2,"0")} ${ampm}`;
                  return (
                    <button key={t} onClick={()=>setSelectedTime(t)} style={{padding:"8px 4px",background:selectedTime===t?C.gold:"#fff",color:selectedTime===t?"#fff":C.text,border:"1px solid "+(selectedTime===t?C.gold:C.border),borderRadius:6,fontSize:12,fontWeight:600,cursor:"pointer"}}>{label}</button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={onClose} style={{flex:1,padding:"12px",background:"#eee",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>Cancel</button>
          <button onClick={submit} disabled={loading || !newDate || !selectedTime} style={{flex:1,padding:"12px",background: (newDate && selectedTime)?C.gold:"#ccc",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:(newDate && selectedTime)?"pointer":"not-allowed"}}>Confirm Reschedule</button>
        </div>

        {error && <div style={{fontSize:11,color:C.red,marginTop:10}}>{error}</div>}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function PackagesTab({ packages, memberships }) {
  const active = packages.filter(p => (p.sessions_remaining || 0) > 0);
  const used = packages.filter(p => (p.sessions_remaining || 0) === 0);

  return (
    <div>
      {memberships.length > 0 && (
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:C.purple,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>💎 Memberships</div>
          {memberships.map(m => (
            <div key={m.id} style={{background:"linear-gradient(135deg, "+C.purpleL+", #fff)",borderRadius:14,padding:"20px",border:"1px solid "+C.purple+"30",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                <div>
                  <div style={{fontSize:18,fontWeight:700,color:C.text,fontFamily:S}}>{m.membership_plans?.name || "Member"}</div>
                  <div style={{fontSize:11,color:C.muted,marginTop:2}}>${m.membership_plans?.monthly_price || 0}/month</div>
                </div>
                <span style={{padding:"4px 10px",background: m.status === "active" ? C.greenL : C.light,color: m.status === "active" ? C.green : C.muted,borderRadius:50,fontSize:9,fontWeight:700,textTransform:"uppercase",height:"fit-content"}}>{m.status}</span>
              </div>
              {m.next_billing_at && <div style={{fontSize:10,color:C.muted}}>Next billing: {new Date(m.next_billing_at).toLocaleDateString()}</div>}
              {m.membership_plans?.benefits && (
                <div style={{marginTop:12,padding:"10px 12px",background:"rgba(255,255,255,0.6)",borderRadius:8}}>
                  <div style={{fontSize:10,color:C.purple,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>Your Benefits</div>
                  <div style={{fontSize:11,color:C.text,whiteSpace:"pre-wrap"}}>{m.membership_plans.benefits}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{marginBottom:16}}>
        <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>📦 Active Packages</div>
        {active.length === 0 ? (
          <div style={{padding:30,textAlign:"center",background:"#fff",borderRadius:14,border:"1px dashed "+C.border,color:C.muted,fontSize:12}}>No active packages</div>
        ) : (
          active.map(p => {
            const total = (p.sessions_used || 0) + (p.sessions_remaining || 0);
            const pct = total > 0 ? Math.round((p.sessions_remaining / total) * 100) : 0;
            return (
              <div key={p.id} style={{background:"#fff",borderRadius:14,padding:"16px 20px",border:"1px solid "+C.border,marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:700,color:C.text,fontFamily:S,marginBottom:3}}>{p.packages?.name || "Package"}</div>
                    <div style={{fontSize:10,color:C.muted}}>Purchased {new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:32,fontWeight:800,color:C.gold,fontFamily:S,lineHeight:1}}>{p.sessions_remaining || 0}</div>
                    <div style={{fontSize:9,color:C.muted,fontWeight:700,letterSpacing:"0.05em"}}>OF {total} LEFT</div>
                  </div>
                </div>
                <div style={{height:8,background:C.light,borderRadius:50,overflow:"hidden"}}>
                  <div style={{height:"100%",width: pct + "%",background:"linear-gradient(90deg,"+C.gold+","+C.goldL+")",borderRadius:50,transition:"width 0.5s"}}/>
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:6,textAlign:"center"}}>{p.sessions_used || 0} used · {p.sessions_remaining || 0} remaining</div>
              </div>
            );
          })
        )}
      </div>

      {used.length > 0 && (
        <div>
          <div style={{fontSize:12,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>📦 Completed</div>
          {used.map(p => (
            <div key={p.id} style={{background:C.light,borderRadius:10,padding:"12px 16px",border:"1px solid "+C.border,marginBottom:6,display:"flex",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:C.text}}>{p.packages?.name || "Package"}</div>
                <div style={{fontSize:10,color:C.muted}}>{p.sessions_used || 0} sessions complete</div>
              </div>
              <span style={{fontSize:18}}>✓</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
function ConsentsTab({ token, signedConsents, pendingConsents, clientName, onSigned }) {
  const [signing, setSigning] = useState(null);
  const [viewing, setViewing] = useState(null);

  return (
    <div>
      {pendingConsents.length > 0 && (
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:700,color:C.red,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>⚠️ Needs Your Signature ({pendingConsents.length})</div>
          {pendingConsents.map(c => (
            <div key={c.id} style={{background:"linear-gradient(135deg, rgba(220,38,38,0.06), #fff)",borderRadius:12,padding:"16px 18px",border:"1px solid "+C.red+"40",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:3}}>{c.name}</div>
                  <div style={{fontSize:11,color:C.muted}}>{(c.content || "").substring(0, 100)}...</div>
                </div>
                <button onClick={()=>setSigning(c)} style={{padding:"10px 18px",background:"linear-gradient(135deg,"+C.red+",#a82a1f)",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F,whiteSpace:"nowrap"}}>✍️ Sign Now</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div style={{fontSize:12,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>✅ Signed Forms ({signedConsents.length})</div>
        {signedConsents.length === 0 ? (
          <div style={{padding:30,textAlign:"center",background:"#fff",borderRadius:14,border:"1px dashed "+C.border,color:C.muted,fontSize:12}}>No signed forms yet</div>
        ) : (
          signedConsents.map(c => (
            <div key={c.id} onClick={()=>setViewing(c)} style={{background:"#fff",borderRadius:12,padding:"14px 16px",border:"1px solid "+C.border,borderLeft:"3px solid "+C.green,marginBottom:8,cursor:"pointer",transition:"all 0.15s"}}
              onMouseEnter={e=>e.currentTarget.style.background=C.greenL}
              onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:2}}>{c.template_name}</div>
                  <div style={{fontSize:10,color:C.muted}}>Signed {new Date(c.signed_at).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</div>
                </div>
                <span style={{fontSize:11,color:C.green,fontWeight:700}}>View →</span>
              </div>
            </div>
          ))
        )}
      </div>

      {signing && <SignConsentModal consent={signing} clientName={clientName} token={token} onClose={()=>setSigning(null)} onSigned={()=>{setSigning(null); onSigned();}}/>}
      {viewing && <ViewConsentModal consent={viewing} onClose={()=>setViewing(null)}/>}
    </div>
  );
}

function SignConsentModal({ consent, clientName, token, onClose, onSigned }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [typedName, setTypedName] = useState(clientName || "");
  const [mode, setMode] = useState("draw"); // 'draw' | 'type'
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (mode === "draw" && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.strokeStyle = "#1a1a2e";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
    }
  }, [mode]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x: x * (canvas.width / rect.width), y: y * (canvas.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); setDrawing(true); const {x,y} = getPos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x,y); };
  const draw = (e) => { if (!drawing) return; e.preventDefault(); const {x,y} = getPos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x,y); ctx.stroke(); setHasSignature(true); };
  const stop = () => setDrawing(false);
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.clearRect(0,0,c.width,c.height); setHasSignature(false); };

  const submit = async () => {
    if (!agreed) { alert("Please confirm you agree to the terms"); return; }
    if (mode === "draw" && !hasSignature) { alert("Please draw your signature"); return; }
    if (mode === "type" && !typedName.trim()) { alert("Please type your name"); return; }

    setSubmitting(true);
    try {
      const sig_data = mode === "draw" ? canvasRef.current.toDataURL("image/png") : null;
      const typed = mode === "type" ? typedName.trim() : null;

      const { data: result, error } = await supabase.functions.invoke("portal-api", {
        body: { action: "sign_consent", token, template_id: consent.id, signature_data: sig_data, typed_name: typed },
      });
      if (error || result?.error) throw new Error(result?.error || error.message);
      onSigned();
    } catch (e) { alert("Failed: " + e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,maxWidth:560,width:"100%",maxHeight:"92vh",overflow:"auto"}}>
        <div style={{padding:"16px 20px",borderBottom:"1px solid "+C.border,background:C.goldBg,position:"sticky",top:0,zIndex:5}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S}}>{consent.name}</div>
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:22}}>×</button>
          </div>
        </div>

        <div style={{padding:20}}>
          <div style={{padding:"16px 18px",background:C.light,borderRadius:10,marginBottom:16,maxHeight:280,overflow:"auto",fontSize:12,color:C.text,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{consent.content}</div>

          <div style={{padding:"12px 14px",background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10,marginBottom:14}}>
            <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
              <input type="checkbox" checked={agreed} onChange={e=>setAgreed(e.target.checked)} style={{marginTop:2,accentColor:C.gold,width:18,height:18}}/>
              <span style={{fontSize:12,color:C.text,fontWeight:600}}>I have read and agree to the terms above. By signing below, I provide my legal consent.</span>
            </label>
          </div>

          {/* Mode toggle */}
          <div style={{display:"flex",gap:6,marginBottom:10,background:C.light,padding:4,borderRadius:8}}>
            <button onClick={()=>setMode("draw")} style={{flex:1,padding:"8px",background: mode==="draw"?"#fff":"transparent",color: mode==="draw"?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow: mode==="draw"?"0 1px 3px rgba(0,0,0,0.06)":"none"}}>✍️ Draw</button>
            <button onClick={()=>setMode("type")} style={{flex:1,padding:"8px",background: mode==="type"?"#fff":"transparent",color: mode==="type"?C.gold:C.muted,border:"none",borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F,boxShadow: mode==="type"?"0 1px 3px rgba(0,0,0,0.06)":"none"}}>⌨️ Type</button>
          </div>

          {mode === "draw" ? (
            <div>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",marginBottom:6}}>Sign Below</div>
              <div style={{position:"relative",border:"2px dashed "+C.borderGold,borderRadius:10,background:"#fff",overflow:"hidden",height:140}}>
                <canvas ref={canvasRef} width={600} height={200} style={{width:"100%",height:"100%",cursor:"crosshair",touchAction:"none"}}
                  onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
                  onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}/>
                {!hasSignature && <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none",color:C.dim,fontStyle:"italic",fontSize:13}}>Sign here</div>}
                <button onClick={clear} style={{position:"absolute",top:8,right:8,padding:"4px 10px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:6,fontSize:10,cursor:"pointer",fontFamily:F}}>Clear</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{fontSize:10,color:C.muted,fontWeight:700,textTransform:"uppercase",marginBottom:6}}>Type Your Full Name</div>
              <input value={typedName} onChange={e=>setTypedName(e.target.value)} placeholder="Your full legal name" style={{width:"100%",padding:"14px 18px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:18,color:C.text,fontFamily:S,fontWeight:600,outline:"none",boxSizing:"border-box"}}/>
              <div style={{fontSize:10,color:C.muted,marginTop:6,fontStyle:"italic"}}>Your typed name has the same legal effect as a handwritten signature</div>
            </div>
          )}

          <div style={{display:"flex",gap:8,marginTop:18}}>
            <button onClick={onClose} style={{flex:1,padding:"12px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
            <button onClick={submit} disabled={submitting || !agreed} style={{flex:2,padding:"12px",background: submitting||!agreed ? C.light : "linear-gradient(135deg,"+C.green+",#1f6b40)",color: submitting||!agreed ? C.muted : "#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor: submitting||!agreed ? "not-allowed":"pointer",fontFamily:F}}>
              {submitting ? "Signing..." : "✓ Sign & Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewConsentModal({ consent, onClose }) {
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:"#fff",borderRadius:16,maxWidth:560,width:"100%",maxHeight:"92vh",overflow:"auto"}}>
        <div style={{padding:"16px 20px",borderBottom:"1px solid "+C.border,background:C.greenL,position:"sticky",top:0,zIndex:5}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:18,fontWeight:800,color:C.green,fontFamily:S,marginBottom:3}}>✅ {consent.template_name}</div>
              <div style={{fontSize:10,color:C.muted}}>Signed {new Date(consent.signed_at).toLocaleString()}</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:22}}>×</button>
          </div>
        </div>

        <div style={{padding:20}}>
          <div style={{padding:"16px 18px",background:C.light,borderRadius:10,marginBottom:16,fontSize:12,color:C.text,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{consent.content}</div>

          <div style={{padding:"14px 16px",background:C.greenL,border:"1px solid "+C.green+"40",borderRadius:10}}>
            <div style={{fontSize:10,color:C.green,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>✓ Signed By</div>
            {consent.signature_data ? (
              <img src={consent.signature_data} alt="Signature" style={{maxHeight:80,background:"#fff",borderRadius:6,padding:4,maxWidth:"100%"}}/>
            ) : (
              <div style={{fontSize:18,fontFamily:S,fontWeight:600,color:C.text,fontStyle:"italic"}}>{consent.typed_name}</div>
            )}
            <div style={{fontSize:10,color:C.muted,marginTop:8}}>{consent.client_name} · {new Date(consent.signed_at).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────
function PaymentsTab({ payments, totalSpent }) {
  const successful = payments.filter(p => p.status === "succeeded");
  return (
    <div>
      <div style={{background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",borderRadius:14,padding:"20px",marginBottom:14,textAlign:"center"}}>
        <div style={{fontSize:11,opacity:0.85,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Total Lifetime Spend</div>
        <div style={{fontSize:36,fontWeight:800,fontFamily:S}}>${totalSpent.toFixed(2)}</div>
        <div style={{fontSize:11,opacity:0.85,marginTop:4}}>{successful.length} transactions</div>
      </div>

      {payments.length === 0 ? (
        <div style={{padding:40,textAlign:"center",background:"#fff",borderRadius:14,border:"1px dashed "+C.border}}>
          <div style={{fontSize:36,marginBottom:8}}>💳</div>
          <div style={{fontSize:13,color:C.muted}}>No payment history yet</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {payments.map(p => (
            <div key={p.id} style={{background:"#fff",borderRadius:10,padding:"14px 16px",border:"1px solid "+C.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:2}}>{p.description || "Payment"}</div>
                <div style={{fontSize:10,color:C.muted}}>{new Date(p.created_at).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})} · {(p.payment_method || "").replace(/_/g," ")}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:14,fontWeight:700,color: p.status === "succeeded" ? C.green : C.red}}>${(+p.amount).toFixed(2)}</div>
                <div style={{fontSize:9,color:C.muted,fontWeight:600,textTransform:"uppercase"}}>{p.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────
function ProfileTab({ client, token, onUpdate }) {
  const [form, setForm] = useState({
    email: client.email || "",
    phone: client.phone || "",
    address: client.address || "",
    dob: client.dob || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke("portal-api", {
        body: { action: "update_profile", token, updates: form },
      });
      if (error) throw error;
      setMsg("✓ Saved!"); setTimeout(()=>setMsg(""), 2000);
      onUpdate();
    } catch (e) { setMsg("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{background:"#fff",borderRadius:14,padding:"20px",border:"1px solid "+C.border,marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:14}}>Personal Info</div>
        <Field label="First Name"><div style={{padding:"10px 14px",background:C.light,borderRadius:8,fontSize:13,color:C.muted}}>{client.first_name || "—"}</div></Field>
        <Field label="Last Name"><div style={{padding:"10px 14px",background:C.light,borderRadius:8,fontSize:13,color:C.muted}}>{client.last_name || "—"}</div></Field>
        <Field label="Email"><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} style={inp}/></Field>
        <Field label="Phone"><input type="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} style={inp}/></Field>
        <Field label="Date of Birth"><input type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} style={inp}/></Field>
        <Field label="Address"><textarea value={form.address} onChange={e=>setForm({...form,address:e.target.value})} rows={2} style={{...inp,resize:"vertical"}}/></Field>

        {msg && <div style={{padding:"10px 14px",background: msg.startsWith("Error") ? C.redL : C.greenL, color: msg.startsWith("Error") ? C.red : C.green,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:10}}>{msg}</div>}
        <button onClick={save} disabled={saving} style={{width:"100%",padding:"12px",background: saving ? C.light : "linear-gradient(135deg,"+C.gold+","+C.goldD+")",color: saving ? C.muted : "#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor: saving?"not-allowed":"pointer",fontFamily:F}}>{saving ? "Saving..." : "💾 Save Changes"}</button>
      </div>
    </div>
  );
}

// Helpers
const inp = {width:"100%",padding:"10px 14px",background:C.light,border:"1px solid "+C.border,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{display:"block",fontSize:10,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>{label}</label>
      {children}
    </div>
  );
}

function tabBtn(active) {
  return {padding:"8px 16px",background: active ? "linear-gradient(135deg,"+C.gold+","+C.goldD+")" : C.light,color: active ? "#fff" : C.muted,border:"none",borderRadius:50,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:F};
}
